import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, Brain, Bone, Shield, Sparkles, Activity,
  Calendar, ChevronRight, TrendingUp, TrendingDown,
  Sun, Moon, Star, Zap, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

// Beautiful animated background
const GradientBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950" />
    <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" />
    <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
    <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-500" />
  </div>
);

// Status badge component
const StatusBadge = ({ status, size = 'md' }) => {
  const configs = {
    excellent: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', ring: 'ring-emerald-500/30' },
    good: { bg: 'bg-green-500/20', text: 'text-green-400', ring: 'ring-green-500/30' },
    balanced: { bg: 'bg-blue-500/20', text: 'text-blue-400', ring: 'ring-blue-500/30' },
    sensitive: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', ring: 'ring-yellow-500/30' },
    weak: { bg: 'bg-red-500/20', text: 'text-red-400', ring: 'ring-red-500/30' },
    strong: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', ring: 'ring-emerald-500/30' }
  };
  
  const config = configs[status] || configs.balanced;
  const sizeClass = size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-sm px-3 py-1';
  
  return (
    <span className={`${config.bg} ${config.text} ${config.ring} ${sizeClass} rounded-full ring-1 font-medium capitalize`}>
      {status}
    </span>
  );
};

// Body system card
const BodySystemCard = ({ system, index }) => {
  const getStatusColor = (status) => {
    switch(status) {
      case 'strong': return 'from-emerald-500 to-green-500';
      case 'sensitive': return 'from-yellow-500 to-orange-500';
      case 'weak': return 'from-red-500 to-rose-500';
      default: return 'from-blue-500 to-cyan-500';
    }
  };

  const getProgressWidth = (status) => {
    switch(status) {
      case 'strong': return '90%';
      case 'balanced': return '70%';
      case 'sensitive': return '50%';
      case 'weak': return '30%';
      default: return '60%';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="relative group"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-white/10 rounded-2xl blur-xl group-hover:blur-2xl transition-all opacity-0 group-hover:opacity-100" />
      
      <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4 hover:border-white/20 transition-all">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{system.icon}</span>
            <div>
              <h4 className="text-white font-medium">{system.name}</h4>
              <p className="text-white/50 text-xs">{system.description}</p>
            </div>
          </div>
          <StatusBadge status={system.status} size="sm" />
        </div>
        
        {/* Progress bar */}
        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: getProgressWidth(system.status) }}
            transition={{ duration: 1, delay: index * 0.1 }}
            className={`h-full bg-gradient-to-r ${getStatusColor(system.status)} rounded-full`}
          />
        </div>
      </div>
    </motion.div>
  );
};

// Main Dashboard Component
const RealHealthDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [healthData, setHealthData] = useState(null);
  const [futureData, setFutureData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Get birth data from localStorage or use default
  const getBirthData = () => {
    try {
      const stored = localStorage.getItem('birthData');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convert to API format
        const dateParts = parsed.date?.split('-') || ['1997', '05', '14'];
        const timeParts = parsed.time?.split(':') || ['12', '00'];
        return {
          name: parsed.full_name || 'User',
          year: parseInt(dateParts[0]),
          month: parseInt(dateParts[1]),
          day: parseInt(dateParts[2]),
          hour: parseInt(timeParts[0]),
          minute: parseInt(timeParts[1]),
          latitude: parsed.latitude || 28.6139,
          longitude: parsed.longitude || 77.2090,
          gender: parsed.gender?.toLowerCase() || 'unknown'
        };
      }
    } catch (e) {
      console.error('Error parsing birth data:', e);
    }
    
    // Default birth data
    return {
      name: 'User',
      year: 1997,
      month: 5,
      day: 14,
      hour: 12,
      minute: 0,
      latitude: 30.32,
      longitude: 78.03,
      gender: 'male'
    };
  };

  const fetchHealthData = async () => {
    setLoading(true);
    const birthData = getBirthData();
    
    try {
      // Fetch all health data in parallel
      const [dashboardRes, futureRes, monthlyRes] = await Promise.all([
        axios.post(`${BACKEND_URL}/api/health/dashboard-card`, birthData),
        axios.post(`${BACKEND_URL}/api/health/future-predictions`, birthData),
        axios.post(`${BACKEND_URL}/api/health/monthly-calendar/${new Date().getFullYear()}`, birthData)
      ]);
      
      setHealthData(dashboardRes.data);
      setFutureData(futureRes.data);
      setMonthlyData(monthlyRes.data);
      
      toast.success('Health data loaded! 🎉');
    } catch (error) {
      console.error('Error fetching health data:', error);
      toast.error('Failed to load health data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealthData();
  }, []);

  const getOverallScore = () => {
    if (!healthData?.body_systems) return 75;
    const scores = healthData.body_systems.map(s => {
      switch(s.status) {
        case 'strong': return 95;
        case 'balanced': return 75;
        case 'sensitive': return 55;
        case 'weak': return 35;
        default: return 65;
      }
    });
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  };

  return (
    <div className="min-h-screen text-white">
      <GradientBackground />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-emerald-400 via-green-400 to-teal-400 bg-clip-text text-transparent mb-2">
            Health & Longevity
          </h1>
          <p className="text-white/60">Your personalized health intelligence</p>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex justify-center gap-2 mb-8">
          {['overview', 'body', 'timeline'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-2 rounded-full transition-all capitalize ${
                activeTab === tab
                  ? 'bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/30'
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab}
            </button>
          ))}
          <button
            onClick={fetchHealthData}
            disabled={loading}
            className="px-4 py-2 rounded-full text-white/60 hover:text-white hover:bg-white/5 transition-all"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
          </div>
        ) : (
          <AnimatePresence mode="wait">
            {/* Overview Tab */}
            {activeTab === 'overview' && healthData && (
              <motion.div
                key="overview"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                {/* Main Score Card */}
                <div className="grid md:grid-cols-3 gap-6">
                  {/* Score Circle */}
                  <div className="md:col-span-1">
                    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 text-center">
                      <div className="relative w-40 h-40 mx-auto">
                        <svg className="w-full h-full -rotate-90">
                          <circle
                            cx="80"
                            cy="80"
                            r="70"
                            fill="none"
                            stroke="rgba(255,255,255,0.1)"
                            strokeWidth="12"
                          />
                          <motion.circle
                            cx="80"
                            cy="80"
                            r="70"
                            fill="none"
                            stroke="url(#gradient)"
                            strokeWidth="12"
                            strokeLinecap="round"
                            strokeDasharray={440}
                            initial={{ strokeDashoffset: 440 }}
                            animate={{ strokeDashoffset: 440 - (440 * getOverallScore() / 100) }}
                            transition={{ duration: 1.5 }}
                          />
                          <defs>
                            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" stopColor="#10b981" />
                              <stop offset="100%" stopColor="#14b8a6" />
                            </linearGradient>
                          </defs>
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-4xl font-bold text-white">{getOverallScore()}</span>
                          <span className="text-white/50 text-sm">Overall</span>
                        </div>
                      </div>
                      
                      <div className="mt-4">
                        <StatusBadge status={healthData.overall_status.toLowerCase().replace(' ', '')} />
                      </div>
                    </div>
                  </div>

                  {/* Constitution & Focus */}
                  <div className="md:col-span-2 space-y-4">
                    {/* Constitution Card */}
                    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                          <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white">Your Constitution</h3>
                          <p className="text-white/50 text-sm">{healthData.constitution}</p>
                        </div>
                      </div>
                    </div>

                    {/* Focus Area */}
                    <div className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 backdrop-blur-xl border border-emerald-500/30 rounded-2xl p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-500/30 flex items-center justify-center">
                          <Activity className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div>
                          <h3 className="text-sm text-emerald-400 font-medium">This Month's Focus</h3>
                          <p className="text-xl font-semibold text-white">{healthData.focus_area}</p>
                        </div>
                      </div>
                    </div>

                    {/* Sensitive & Strong Areas */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4">
                        <h4 className="text-yellow-400 text-sm font-medium mb-2 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-yellow-400" />
                          Sensitive Areas
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {healthData.sensitive_areas?.slice(0, 3).map((area, i) => (
                            <span key={i} className="text-xs bg-yellow-500/10 text-yellow-400 px-2 py-1 rounded-full">
                              {area}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4">
                        <h4 className="text-emerald-400 text-sm font-medium mb-2 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-emerald-400" />
                          Strong Areas
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {healthData.strong_areas?.slice(0, 3).map((area, i) => (
                            <span key={i} className="text-xs bg-emerald-500/10 text-emerald-400 px-2 py-1 rounded-full">
                              {area}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Future Outlook */}
                {futureData?.predictions && (
                  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-6">
                    <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-emerald-400" />
                      5-Year Health Outlook
                    </h3>
                    
                    <div className="grid grid-cols-5 gap-4">
                      {futureData.predictions.slice(0, 5).map((pred, i) => (
                        <motion.div
                          key={pred.year}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: i * 0.1 }}
                          className="text-center"
                        >
                          <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center text-2xl mb-2 ${
                            pred.level === 'excellent' ? 'bg-emerald-500/20' :
                            pred.level === 'good' ? 'bg-green-500/20' :
                            pred.level === 'moderate' ? 'bg-yellow-500/20' :
                            'bg-orange-500/20'
                          }`}>
                            {pred.color}
                          </div>
                          <p className="text-white font-semibold">{pred.year}</p>
                          <p className="text-white/50 text-xs capitalize">{pred.level}</p>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Body Systems Tab */}
            {activeTab === 'body' && healthData && (
              <motion.div
                key="body"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {healthData.body_systems?.map((system, index) => (
                    <BodySystemCard key={system.name} system={system} index={index} />
                  ))}
                </div>
              </motion.div>
            )}

            {/* Timeline Tab */}
            {activeTab === 'timeline' && monthlyData && (
              <motion.div
                key="timeline"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-6">
                  <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-emerald-400" />
                    {monthlyData.year} Monthly Calendar
                  </h3>
                  
                  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {monthlyData.months?.map((month, index) => (
                      <motion.div
                        key={month.month}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05 }}
                        className={`p-4 rounded-2xl text-center cursor-pointer transition-all hover:scale-105 ${
                          month.quality === 'favorable' ? 'bg-emerald-500/10 border border-emerald-500/30' :
                          month.quality === 'careful' ? 'bg-yellow-500/10 border border-yellow-500/30' :
                          'bg-white/5 border border-white/10'
                        }`}
                      >
                        <span className="text-2xl mb-2 block">{month.icon}</span>
                        <p className="text-white font-medium">{month.month_name?.slice(0, 3)}</p>
                        <p className="text-white/50 text-xs capitalize">{month.quality}</p>
                        <p className="text-white/40 text-xs mt-1">{month.focus}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default RealHealthDashboard;
